import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, Alert, Tooltip, Grid, Switch, FormControlLabel, Divider
} from '@mui/material';
import { Add, Delete, Edit, Lock, People } from '@mui/icons-material';
import { getRoles, createRole, updateRole, deleteRole, getPermissionRegistry } from '../api';
import { useAuth } from '../context/AuthContext';
import { useConfirmDialog } from './ConfirmDialog';

export default function RoleManagement() {
  const { hasPermission } = useAuth();
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  const [roles, setRoles] = useState([]);
  const [registry, setRegistry] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', permissions: {} });

  const canManage = hasPermission('can_manage_roles');

  const loadData = useCallback(async () => {
    try {
      const [rolesRes, registryRes] = await Promise.all([getRoles(), getPermissionRegistry()]);
      setRoles(rolesRes.data || []);
      setRegistry(registryRes.data || {});
    } catch (err) {
      setError('Failed to load roles');
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleOpenCreate = () => {
    const defaultPerms = {};
    Object.keys(registry).forEach(k => { defaultPerms[k] = false; });
    setFormData({ name: '', description: '', permissions: defaultPerms });
    setEditingRole(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (role) => {
    const perms = typeof role.permissions === 'string' ? JSON.parse(role.permissions) : (role.permissions || {});
    // Ensure all registry keys present
    const fullPerms = {};
    Object.keys(registry).forEach(k => { fullPerms[k] = perms[k] === true; });
    setFormData({ name: role.name, description: role.description || '', permissions: fullPerms });
    setEditingRole(role);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      if (editingRole) {
        await updateRole(editingRole.id, formData);
        setSuccess(`Role "${formData.name}" updated`);
      } else {
        await createRole(formData);
        setSuccess(`Role "${formData.name}" created`);
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save role');
    }
  };

  const handleDelete = async (role) => {
    const confirmed = await showConfirm({
      title: 'Delete Role',
      message: `Delete role "${role.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteRole(role.id);
      setSuccess(`Role "${role.name}" deleted`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete role');
    }
  };

  const togglePermission = (key) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] }
    }));
  };

  const toggleAll = (enabled) => {
    const perms = {};
    Object.keys(registry).forEach(k => { perms[k] = enabled; });
    setFormData(prev => ({ ...prev, permissions: perms }));
  };

  // Group permissions by category for display
  const permissionsByCategory = {};
  Object.entries(registry).forEach(([key, meta]) => {
    if (!permissionsByCategory[meta.category]) permissionsByCategory[meta.category] = [];
    permissionsByCategory[meta.category].push({ key, ...meta });
  });

  const enabledCount = (perms) => {
    const p = typeof perms === 'string' ? JSON.parse(perms) : (perms || {});
    return Object.values(p).filter(v => v === true).length;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Role Management</Typography>
        {canManage && (
          <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
            Create Role
          </Button>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage roles and permissions for your organization. Preset roles cannot be modified but can be used as templates for custom roles.
      </Typography>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Permissions</TableCell>
              <TableCell align="center">Users</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map(role => {
              const perms = typeof role.permissions === 'string' ? JSON.parse(role.permissions) : (role.permissions || {});
              return (
                <TableRow key={role.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{role.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{role.description || '—'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={`${enabledCount(perms)} / ${Object.keys(registry).length}`}
                      color={enabledCount(perms) === Object.keys(registry).length ? 'success' : enabledCount(perms) > 0 ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" icon={<People />} label={role.user_count || 0} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {role.is_preset ? (
                      <Chip size="small" icon={<Lock />} label="Preset" variant="outlined" />
                    ) : (
                      <Chip size="small" label="Custom" color="info" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {canManage && !role.is_preset && (
                      <>
                        <Tooltip title="Edit role">
                          <IconButton size="small" color="primary" onClick={() => handleOpenEdit(role)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete role">
                          <IconButton size="small" color="error" onClick={() => handleDelete(role)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {role.is_preset && (
                      <Tooltip title="View permissions">
                        <IconButton size="small" onClick={() => handleOpenEdit(role)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRole ? (editingRole.is_preset ? `View Preset Role: ${editingRole.name}` : `Edit Role: ${editingRole.name}`) : 'Create Custom Role'}
        </DialogTitle>
        <DialogContent>
          {!editingRole?.is_preset && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Role Name" value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Description" value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} />
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2">Permissions</Typography>
            {!editingRole?.is_preset && (
              <Box>
                <Button size="small" onClick={() => toggleAll(true)}>Enable All</Button>
                <Button size="small" onClick={() => toggleAll(false)}>Disable All</Button>
              </Box>
            )}
          </Box>

          {Object.entries(permissionsByCategory).map(([category, perms]) => (
            <Box key={category} sx={{ mb: 2 }}>
              <Divider sx={{ mb: 1 }}><Typography variant="caption" color="text.secondary">{category}</Typography></Divider>
              <Grid container spacing={0}>
                {perms.map(perm => (
                  <Grid item xs={12} sm={6} key={perm.key}>
                    <FormControlLabel
                      control={
                        <Switch size="small"
                          checked={formData.permissions[perm.key] === true}
                          onChange={() => togglePermission(perm.key)}
                          disabled={editingRole?.is_preset}
                        />
                      }
                      label={
                        <Tooltip title={perm.description} placement="right">
                          <Typography variant="body2">{perm.label}</Typography>
                        </Tooltip>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {editingRole?.is_preset ? 'Close' : 'Cancel'}
          </Button>
          {!editingRole?.is_preset && (
            <Button variant="contained" onClick={handleSave} disabled={!formData.name.trim()}>
              {editingRole ? 'Save Changes' : 'Create Role'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
